import {
    ArrowBack,
    ArrowForward,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Container,
    Grid,
    IconButton,
    LinearProgress,
    Modal,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import parse from 'html-react-parser';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReturnBack from '../../components/reusable/accordions/ReturnBack';
import SEO from '../../components/reusable/SEO';
import { useCourses } from '../../context/CourseContext';
import VideoPlayer from './VideoPlayer'; // Assuming you have this component
import { UserSettingsContext } from '../../context/UserSettingsContext';
import LessonSkeleton from '../../components/skeleton/lms/LessonSkeleton';

function LessonPage() {
    const { courseId, lessonId } = useParams();
    const {
        courses,
        getLesson,
        lesson,
        topics,
        getCourseTopics,
        fetchCourses,
    } = useCourses();

    const navigate = useNavigate();
    const [videoCompleted, setVideoCompleted] = useState(false);
    const [nextLesson, setNextLesson] = useState(null);
    const [prevLesson, setPrevLesson] = useState(null);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [key, setKey] = useState(0); // Add a key to force VideoPlayer remount
    const { userSettings } = useContext(UserSettingsContext);
    const [currentLessonIndex, setCurrentLessonIndex] = useState(-1);
    const [course, setCourse] = useState(null);

    useEffect(() => {
        if (courses.length > 0 && courseId) {
            const selectedCourse = courses.find((c) => c.slug === courseId);
            setCourse(selectedCourse);
        } else {
            fetchCourses();
        }
    }, [courses]);

    useEffect(() => {
        if (topics.length === 0 && course) getCourseTopics(course.id);
    }, [topics, course]);

    useEffect(() => {
        if (!topics.length || !lessonId) return;

        const currentIndex = topics.findIndex(
            (topic) => topic.id.toString() === lessonId,
        );
        setCurrentLessonIndex(currentIndex);

        const navigableTopics = topics.filter(
            (topic) => topic.id !== 1 && topic.id !== 2 && topic.id !== 3,
        );

        const navigableIndex = navigableTopics.findIndex(
            (topic) => topic.id.toString() === lessonId,
        );

        setVideoCompleted(navigableTopics[navigableIndex]?.is_completed);

        let prevId = null;
        let nextId = null;

        if (![1, 2, 3].includes(parseInt(lessonId))) {
            prevId =
                navigableIndex > 0
                    ? navigableTopics[navigableIndex - 1].id
                    : null;
            nextId =
                navigableIndex < navigableTopics.length - 1
                    ? navigableTopics[navigableIndex + 1].id
                    : null;
        }
        setPrevLesson(prevId);
        setNextLesson(nextId);
    }, [topics, lessonId]);

    useEffect(() => {
        setKey((prevKey) => prevKey + 1); // Increment key to force VideoPlayer remount
        getLesson(lessonId);
    }, [lessonId]);

    const isNextButtonDisabled = () => {
        // If is_items_controls is 0, always return false
        if (course?.is_items_controls === 0) {
            return false;
        }
        // If is_items_controls is 1
        if (course?.is_items_controls === 1) {
            // If webinar_audit_control is 0, return false
            if (userSettings?.webinar_audit_control === 0) {
                return false;
            }
            // If webinar_audit_control is 1, return !welcomeCompleted
            if (
                userSettings?.webinar_audit_control === 1 ||
                userSettings?.webinar_audit_control === undefined
            ) {
                return !videoCompleted;
            }
        }
    };

    // Handle video completion
    const handleVideoComplete = () => {
        setVideoCompleted(true);
        isNextButtonDisabled();
    };

    const handleBackToCourse = () => {
        navigate(`/webinars/lms/${courseId}`);
    };

    const handleBackToWebinars = () => {
        navigate('/webinars/lms');
    };

    const handleNextLesson = () => {
        if (nextLesson) {
            navigate(`/webinars/lms/${courseId}/${nextLesson}`);
        } else {
            setShowCompletionModal(true);
        }
    };

    const handlePrevLesson = () => {
        if (prevLesson) {
            navigate(`/webinars/lms/${courseId}/${prevLesson}`);
        } else {
            navigate(`/webinars/lms/${courseId}/welcome`);
        }
    };

    // Close modal
    const handleCloseModal = () => {
        setShowCompletionModal(false);
    };

    if (topics.length === 0 || !lesson || !course) {
        return (
            <>
                <LessonSkeleton />
            </>
        );
    }

    return (
        <Box sx={{ position: 'relative' }}>
            <SEO pageTitle={`${lesson.title} | Toolkit.law`} />

            <ReturnBack pageTitle={course?.name ?? ''} />
            <Box sx={{ p: 3 }} />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Video and description side by side */}
                <Grid container spacing={3}>
                    {/* Video column */}
                    <Grid item xs={12} md={8}>
                        <Paper
                            elevation={2}
                            sx={{
                                overflow: 'hidden',
                                borderRadius: 2,
                                height: '100%',
                                border: '1px solid #ccc',
                            }}
                        >
                            <VideoPlayer
                                key={key} // Add key to force remount when lesson changes
                                course={course}
                                videoUrl={lesson.youtube_url}
                                onComplete={handleVideoComplete} // Direct callback for video completion
                                isCompleted={videoCompleted}
                                courseId={course?.id}
                                videoControls={
                                    userSettings?.webinar_audit_control === 1 ||
                                    course?.is_items_controls === 1
                                }
                                courseItemId={lesson.id}
                            />
                        </Paper>
                    </Grid>

                    {/* Description column */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            sx={{
                                p: 3,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',

                                overflowY: 'auto',
                                border: '1px solid #ccc',
                            }}
                        >
                            <Typography
                                variant="h5"
                                component="h2"
                                gutterBottom
                            >
                                {lesson.title}
                            </Typography>
                            <Box
                                sx={{
                                    overflowY: 'auto',
                                    flex: 1,
                                    pr: 1,
                                    mt: 1,
                                }}
                            >
                                <Typography variant="body1">
                                    {parse(
                                        lesson.description
                                            ? lesson.description
                                            : '',
                                    )}
                                </Typography>
                            </Box>
                            {/* Navigation Arrows */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Tooltip title={'Previous Lesson'}>
                                    <IconButton
                                        onClick={() => handlePrevLesson()}
                                        size="small"
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            color: 'black',
                                            borderRadius: '10px',
                                        }}
                                    >
                                        <ArrowBack fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Typography
                                    sx={{ mx: 2, fontWeight: 'medium' }}
                                >
                                    Slides{' '}
                                    {(currentLessonIndex + 4)
                                        .toString()
                                        .padStart(2, '0')}
                                    /
                                    {(topics.length + 1)
                                        .toString()
                                        .padStart(2, '0')}
                                </Typography>
                                <Tooltip title={'Next Lesson'}>
                                    <IconButton
                                        onClick={() => handleNextLesson()}
                                        disabled={isNextButtonDisabled()}
                                        size="small"
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            color: 'black',
                                            borderRadius: '10px',
                                        }}
                                    >
                                        <ArrowForward fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mt: 2,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleBackToWebinars}
                                >
                                    Webinars
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleBackToCourse}
                                >
                                    Slides
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Completion Modal */}
                <Modal
                    open={showCompletionModal}
                    onClose={handleCloseModal}
                    aria-labelledby="completion-modal-title"
                    aria-describedby="completion-modal-description"
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 400 },
                            maxWidth: '100%',
                            outline: 'none',
                        }}
                    >
                        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <Box
                                sx={{
                                    pt: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    color: 'primary.main',
                                    pb: 2,
                                }}
                            >
                                <CheckCircleIcon sx={{ fontSize: 60, mb: 1 }} />
                                <Typography
                                    variant="h5"
                                    component="h2"
                                    id="completion-modal-title"
                                    align="center"
                                >
                                    Congratulations
                                </Typography>
                            </Box>
                            <CardContent sx={{ pt: 0 }}>
                                <Typography
                                    id="completion-modal-description"
                                    variant="body1"
                                    align="center"
                                    sx={{ mb: 2 }}
                                >
                                    You have successfully completed <br />{' '}
                                    <strong>{course.name}</strong>.
                                </Typography>
                            </CardContent>
                            <CardActions
                                sx={{
                                    p: 2,
                                    pt: 0,
                                    justifyContent: 'center',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleBackToCourse}
                                >
                                    Done
                                </Button>
                            </CardActions>
                        </Card>
                    </Box>
                </Modal>
            </Container>
        </Box>
    );
}

export default LessonPage;
